/**
 * Papéis de usuário na obra — fonte única de verdade da v1.
 *
 * O valor técnico é o que vai para `work_users.role` (enum `work_user_role` no
 * Postgres); o rótulo é o que a interface mostra. Antes a tela tinha seis
 * rótulos soltos num array de strings e o banco tinha quatro valores, sem
 * ligação entre os dois — dava para escolher "Diretor" na interface sem que
 * isso significasse nada no banco.
 *
 * `admin`, `engineer`, `foreman` e `viewer` já existiam e foram preservados
 * para não quebrar vínculos gravados nem as policies de RLS que os consultam.
 * `manager` e `administrative` entraram pela migration
 * 20260806180000_work_user_role_v1_seis_papeis.
 */

export const PAPEIS = [
  { valor: "admin", rotulo: "Diretor" },
  { valor: "manager", rotulo: "Gestor" },
  { valor: "engineer", rotulo: "Engenheiro" },
  { valor: "foreman", rotulo: "Mestre" },
  { valor: "administrative", rotulo: "Administrativo" },
  { valor: "viewer", rotulo: "Cliente/Visitante" },
] as const;

export type PapelValor = (typeof PAPEIS)[number]["valor"];
export type PapelRotulo = (typeof PAPEIS)[number]["rotulo"];

const POR_VALOR = new Map<string, string>(PAPEIS.map((p) => [p.valor, p.rotulo]));
const POR_ROTULO = new Map<string, PapelValor>(
  PAPEIS.map((p) => [p.rotulo, p.valor])
);

/** Lista de rótulos, na ordem hierárquica em que a interface os exibe. */
export const ROTULOS_PAPEIS: readonly string[] = PAPEIS.map((p) => p.rotulo);

/**
 * Rótulo de um papel vindo do banco. Papel desconhecido (enum que cresceu sem
 * a interface acompanhar) devolve `undefined` em vez do valor cru — a tela
 * mostra "—" e não vaza `administrative` para o usuário final.
 */
export const rotuloDoPapel = (valor?: string | null): string | undefined =>
  valor ? POR_VALOR.get(valor) : undefined;

/** Valor técnico a partir do rótulo escolhido na interface. */
export const papelDoRotulo = (rotulo?: string | null): PapelValor | undefined =>
  rotulo ? POR_ROTULO.get(rotulo) : undefined;

/** `true` se o valor existe no enum que a v1 conhece. */
export const ehPapelConhecido = (valor?: string | null): valor is PapelValor =>
  !!valor && POR_VALOR.has(valor);
